import { Location } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  ContentChildren,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  QueryList,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { NavigationEnd, Router, Event } from '@angular/router';
import { Subject, pipe, of, Subscription, from } from 'rxjs';
import { mergeAll, takeUntil } from 'rxjs/operators';
import { LegacyRouterLinkDirective } from './legacy-router-link.directive';
import { LegacyRouterService, LegacyRouteState } from './legacy-router.service';

@Directive({
  selector: '[modeLegacyRouterLinkActive]',
  exportAs: 'modeLegacyRouterLinkActive',
})
export class LegacyRouterLinkActiveDirective implements OnDestroy, OnChanges, AfterContentInit {
  private classes: string[] = [];
  private destroy$: Subject<void> = new Subject();
  private linkInputChangesSubscription?: Subscription;
  public readonly isActive: boolean = false;

  @ContentChildren(LegacyRouterLinkDirective, { descendants: true }) links!: QueryList<LegacyRouterLinkDirective>;

  constructor(
    private router: Router,
    private element: ElementRef,
    private renderer: Renderer2,
    private readonly cdr: ChangeDetectorRef,
    private location: Location,
    private legacyRouterService: LegacyRouterService,
    @Optional() private link?: LegacyRouterLinkDirective
  ) {
    // listen to router event changes for updates
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((s: Event) => {
      if (s instanceof NavigationEnd) {
        this.update();
      }
    });
    // listen to legacy router state changes for updates
    this.legacyRouterService
      .getRouteState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.update();
      });
  }

  ngAfterContentInit() {
    // `of(null)` is used to force subscribe body to execute once immediately (like `startWith`).
    of(this.links.changes, of(null))
      .pipe(mergeAll())
      .subscribe((_) => {
        this.update();
        this.subscribeToEachLinkOnChanges();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.linkInputChangesSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.update();
  }

  private subscribeToEachLinkOnChanges() {
    this.linkInputChangesSubscription?.unsubscribe();
    const allLinkChanges = [...this.links.toArray(), this.link]
      // filter on onChanges because mockDirective does not mock properties
      .filter((link): link is LegacyRouterLinkDirective => !!link && link.onChanges != null)
      .map((link) => link.onChanges);
    this.linkInputChangesSubscription = from(allLinkChanges)
      .pipe(mergeAll())
      .subscribe((link) => {
        this.update();
      });
  }

  @Input()
  set modeLegacyRouterLinkActive(data: string[] | string) {
    const classes = Array.isArray(data) ? data : data.split(' ');
    this.classes = classes.filter((c) => !!c);
  }

  private hasActiveLinks(): boolean {
    // An active link either has an href path equal to the browser url
    // or any children legacyRouterLink directives' href path is in the browser url
    return this.location.isCurrentPathEqualTo(this.element.nativeElement.pathname) || this.hasActiveLegacyLinks();
  }

  private hasActiveLegacyLinks(): boolean {
    return this.links?.some(this.isLegacyRouterLinkActive.bind(this));
  }

  private isLegacyRouterLinkActive(link?: LegacyRouterLinkDirective): boolean {
    if (link?.href) {
      return this.location.isCurrentPathEqualTo(link.href);
    }
    return false;
  }

  // This general update logic is similar to the Angular routerLinkActive directive
  // https://github.com/angular/angular/blob/faf9f5a/packages/router/src/directives/router_link_active.ts
  private update() {
    Promise.resolve().then(() => {
      const hasActiveLinks = this.hasActiveLinks();
      if (this.isActive !== hasActiveLinks) {
        (this as any).isActive = hasActiveLinks;
        this.cdr.markForCheck();
        this.classes.forEach((c) => {
          if (hasActiveLinks) {
            this.renderer.addClass(this.element.nativeElement, c);
          } else {
            this.renderer.removeClass(this.element.nativeElement, c);
          }
        });
      }
    });
  }
}
